






















































































import Vue from "vue";
import {Component, Prop, Watch} from 'vue-property-decorator'
import {Decimal} from 'decimal.js';

import {CashoutData, countryHasSkus, countryTags, FieldErrors} from "../models";
import FieldErrorList from './FieldErrorList.vue'
import {cashoutEventBus, CashoutEvents} from "../bus";
import {
    DECIMAL_PLACES_CRYPTO,
    DECIMAL_PLACES_FIAT,
    formatCurrency,
    NEAREST_CRYPTO,
    NEAREST_FIAT,
    ZERO,
} from "../util/number";
import {validateEmail} from "../util/email";

@Component({
    components: {
        FieldErrorList,
    }
})
export default class CashoutWalletForm extends Vue {
    @Prop()
    cashoutData!: CashoutData;
    @Prop()
    isMultiStep!: boolean;

    fieldErrors: FieldErrors = {};

    cryptoStep = Math.pow(10, -DECIMAL_PLACES_CRYPTO);
    fiatStep = Math.pow(10, -DECIMAL_PLACES_FIAT);

    cryptoHasFocus: boolean = false;
    fiatHasFocus: boolean = false;

    cryptoTextValue: string = "";
    fiatTextValue: string = "";

    // noinspection JSUnusedGlobalSymbols
    countryTags = countryTags;
    countryHasSkus = countryHasSkus;

    // noinspection JSUnusedGlobalSymbols
    mounted(): void {
        cashoutEventBus.$on(CashoutEvents.SKU_SELECT, () => this.validateForm());
    }

    get walletIconUrl(): string {
        return `/site-static/img/main/icons/currencies/${this.cashoutData.walletData.walletCurrency}.svg`
    }

    get formattedWalletBalance(): string {
        return formatCurrency(this.cashoutData.walletData.walletBalance, this.cashoutData.walletData.walletCurrency)
    }

    get formattedWalletBalanceAsFiat(): string {
        if (!this.cashoutData.fxRate) return "";
        if (this.cashoutData.walletBalanceAsFiat === undefined) return "";
        return formatCurrency(this.cashoutData.walletBalanceAsFiat, this.cashoutData.fiatCurrencyCode)
    }

    get fiatAmount(): string {
        if (this.fiatHasFocus) return this.fiatTextValue;
        if (this.cashoutData.fiatAmount.eq(0))
            return "";
        return this.cashoutData.fiatAmount.toNearest(NEAREST_FIAT).toFixed(DECIMAL_PLACES_FIAT)
    }

    set fiatAmount(value: string) {
        this.fiatTextValue = value;

        if (!value) {
            this.cashoutData.fiatAmount = new Decimal(0);
            return
        }

        let valueAsDecimal = new Decimal(value).toNearest(NEAREST_FIAT, Decimal.ROUND_DOWN);

        if (valueAsDecimal.lte(0)) {
            this.cashoutData.fiatAmount = new Decimal(0);
            return;
        }

        this.cashoutData.fiatAmount = valueAsDecimal;
    }

    fiatFocus(): void {
        // Override text states
        this.fiatTextValue = this.fiatAmount;
    }

    fiatBlur(): void {
        // Override text states
        this.fiatTextValue = this.fiatAmount;
    }

    get cryptoAmount(): string {
        if (this.cryptoHasFocus) return this.cryptoTextValue;
        if (this.cashoutData.cryptoAmount.eq(0))
            return "";
        return this.cashoutData.cryptoAmount.toNearest(NEAREST_CRYPTO).toFixed()
    }

    set cryptoAmount(value: string) {
        this.cryptoTextValue = value;

        if (!value) {
            this.cashoutData.cryptoAmount = new Decimal(0);
            return
        }

        this.cryptoTextValue = value;

        let valueAsDecimal = new Decimal(value).toNearest(NEAREST_CRYPTO, Decimal.ROUND_DOWN);

        if (valueAsDecimal.lte(0)) {
            this.cashoutData.cryptoAmount = new Decimal(0);
            return;
        }

        this.cashoutData.cryptoAmount = valueAsDecimal;
    }

    cryptoFocus(): void {
        // Override text states
        this.cryptoTextValue = this.cryptoAmount;
    }

    cryptoBlur(): void {
        // Override text states
        this.cryptoTextValue = this.cryptoAmount;
    }

    cashoutMax() {
        this.cashoutData.cryptoAmount = this.cashoutData.walletData.walletBalance;
        this.cryptoHasFocus = false;
        this.fiatHasFocus = false;
    }

    getErrors(field: string): Array<string> {
        if (!this.fieldErrors.hasOwnProperty(field)) return [];
        return this.fieldErrors[field];
    }

    hasErrors(field: string): boolean {
        return this.getErrors(field).length != 0;
    }

    clearErrors(field: string): void {
        if (!this.fieldErrors.hasOwnProperty(field)) return;
        this.fieldErrors[field] = []
    }

    get formValid(): boolean {
        // Are there any field errors?
        for (let key of Object.keys(this.fieldErrors)) {
            if (this.fieldErrors[key].length != 0) return false;
        }
        return true;
    }

    @Watch('cashoutData._cryptoAmount') // Watch the private underlying var, as watching a getter does not work
    @Watch('cashoutData._fiatAmount') // Watch the private underlying var, as watching a getter does not work
    @Watch('cashoutData.walletData') // In case of user wallet changing
    fiatChanged() {
        // Force validation of fields
        this.validateAmountFields();
        if (this.cashoutData.sku) {
            // Unselect sku when value goes out of range
            this.cashoutData.sku.availableForValue(
                this.cashoutData.sku.cashoutValue(this.cashoutData.fiatAmount), this.cashoutData.minFiatAmount || ZERO)
                .then((available => {
                    if (!available) this.cashoutData.selectedSkuId = null;
                }));
        } else if (this.cashoutData.lastSku) {
            // Reselect last sku if value goes back into range
            this.cashoutData.lastSku.availableForValue(
                this.cashoutData.lastSku.cashoutValue(this.cashoutData.fiatAmount), this.cashoutData.minFiatAmount || ZERO)
                .then((available => {
                    if (!this.cashoutData.lastSku!.countryTagCodes.includes(this.cashoutData.cashoutCountryCode)) return;
                    if (available) this.cashoutData.selectedSkuId = this.cashoutData.lastSelectedSkuId;
                }));
        }
    }

    @Watch('cashoutData.cashoutCountryCode')
    skuCountryCodeChanged() {
        // Unselect sku when country changes
        this.cashoutData.selectedSkuId = null;
    }


    validateEmailField() {
        let isValid = validateEmail(this.cashoutData.deliveryEmail);
        this.$set(this.fieldErrors, 'email', []);
        if (!isValid) {
            this.fieldErrors.email.push("Please enter a valid email address")
        }
    }

    validateAmountFields() {
        // Combined as we always want to validate these at the same time
        this.$set(this.fieldErrors, 'cryptoAmount', []);
        this.$set(this.fieldErrors, 'fiatAmount', []);

        // Don't bother validating if no amounts set yet
        if (this.cashoutData.cryptoAmount.eq(0)) return;

        if (this.cashoutData.cryptoAmount.gt(this.cashoutData.walletData.walletBalance)) {
            this.fieldErrors.cryptoAmount.push(`You don't have enough ${this.cashoutData.walletData.walletCurrency} to cover this transaction`)
        }

        if (this.cashoutData.fiatAmount.gt(this.cashoutData.maxFiatAmount)) {
            this.fieldErrors.fiatAmount.push(`Cashout amount must not be greater than ${formatCurrency(this.cashoutData.maxFiatAmount, this.cashoutData.fiatCurrencyCode)}`)
        }

        if (this.cashoutData.cryptoAmount.lt(this.cashoutData.minCryptoAmount)) {
            this.fieldErrors.cryptoAmount.push(`Cashout must be greater than ${this.cashoutData.minCryptoAmount} ${this.cashoutData.cryptoCurrencyCode}`)
        }
    }

    validateForm() {
        this.validateEmailField();
        this.validateAmountFields();
    }

    submitForm() {
        // Form only submits on mobile, on desktop whole three step process is on one screen
        // Force validation
        this.validateForm();
        if (this.formValid) cashoutEventBus.$emit(CashoutEvents.WALLET_FORM_SUBMITTED);
    }
}
